import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AppConfig, APP_CONFIG } from '@app/app-config.module';
import {
  Observable,
  retry,
  Observer,
  switchMap,
  map,
  of,
  tap,
  noop,
  debounceTime,
  distinctUntilChanged,
  timeout,
} from 'rxjs';  
import { StandardResponse, StandardResponseList, StandardResponses } from '../models/standard-response.model';
import { AddressBookEmailSearchResult, AddressBookRequestDtoGet, AddressBookResponseDtoGet, CheckPDFFilePrintandPostRequestDto, CheckPDFFilePrintandPostResponseDto, DeleteAddressBookRequestDtoGet, GetCCTTAAByPostcodeRequestDto, GetCCTTAAByPostcodeResponseDto, GetRoadLaneAlleyByCCAATTRequestDto, GetRoadLaneAlleyByCCAATTResponseDto, PostCodeSearchResult, UpsertAddressBookRequestDto } from '../models/address-book.model';
import { FormControl } from '@angular/forms';
import { StorageUsageInfo } from '../models/storage-usage.model';

interface ObservedPostCodeSource {
  postcode: string,
  valid: boolean
}

interface ObservedEmailSource {
  email: string,
  valid: boolean
}
@Injectable({
  providedIn: 'root'
})
export class AddressBookService {
  constructor(
    @Inject(APP_CONFIG) private config: AppConfig,
    private http: HttpClient
  ) { }

  search(criteria: AddressBookRequestDtoGet,
    pageNumber: number,
    pageSize: number): Observable<StandardResponses<AddressBookResponseDtoGet>> {
    return this.http
      .post<StandardResponses<AddressBookResponseDtoGet>>(`${this.config.apiEndpoint}/address-book/get-pagination`, {
        criteria: criteria,
        pageNumber: pageNumber,
        pageSize: pageSize,
      })
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  searchObserve(email: string): Observable<AddressBookEmailSearchResult> {
    return this.http
      .post<AddressBookEmailSearchResult>(`${this.config.apiEndpoint}/address-book/get-list`, {
        email
      })
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  getCCTTAAByPostcode(postcode: string): Observable<PostCodeSearchResult> {
    return this.http
      .post<PostCodeSearchResult>(`${this.config.apiEndpoint}/ads/getCCTTAAByPostcode`, {
        postcode
      })
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  getRoadLaneAlleyByCCAATT(request: GetRoadLaneAlleyByCCAATTRequestDto): Observable<StandardResponseList<GetRoadLaneAlleyByCCAATTResponseDto>> {
    return this.http
      .post<StandardResponseList<GetRoadLaneAlleyByCCAATTResponseDto>>(`${this.config.apiEndpoint}/ads/getRoadLaneAlleyByCCAATT`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  

  getCCTTAAWithDefaultFirstByPostcodeAsObservable(postcode: FormControl) {
    return new Observable((observer: Observer<ObservedPostCodeSource | undefined>) => {
      observer.next({postcode:postcode.value, valid:postcode.valid});
    }).pipe(
      debounceTime(500),
      distinctUntilChanged((prev, cur) => prev.postcode === cur.postcode),
      switchMap((postcodeSrc: ObservedPostCodeSource) => {
        if (postcodeSrc && postcodeSrc.postcode) {
          return this.getCCTTAAByPostcode(postcodeSrc.postcode).pipe(
            map((response: PostCodeSearchResult) => { 
              if(response && response.code == "200" && response.data && response.data){               
                if(postcodeSrc.valid){ 
                  return [...response.data];
                }else{
                  return response.data;
                }
              }else{
                return [];
              }
            }),
            tap(
              () => noop,
              (err) => {
                console.log((err && err.message) || 'Something went wrong');
              }
            )
          );
        }
        return of([]);
      })
    );
  }

  searchAddressBookByEmailAsObservable(email: FormControl) {
    return new Observable((observer: Observer<ObservedEmailSource | undefined>) => {
      observer.next({email:email.value, valid:email.valid});
    }).pipe(
      debounceTime(500),
      distinctUntilChanged((prev, cur) => prev.email === cur.email),
      switchMap((emailSrc: ObservedEmailSource) => {
        if (emailSrc && emailSrc.email) {
          return this.searchObserve(emailSrc.email).pipe(
            map((response: AddressBookEmailSearchResult) => { 
              if(response && response.code == "200" && response.data && response.data){               
                if(emailSrc.valid){ 
                  return [{
                    id : '$_def',
                    email : emailSrc.email,
                    ownerId : '',
                    cpid : '',
                    addressTo : '',
                    addressStreet : '',
                    addressDistrict : '',
                    addressSubDistrict : '',
                    addressProvince : '',
                    addressPostcode : '',
                    road : '',
                    lane : '',
                    alley : '',
                    addressDistrictCode : '',
                    addressSubDistrictCode : '',
                    addressProvinceCode : '',
                    telNumber : '',
                    createdAt : '',
                    updatedAt : '',
                    companyUserId : null
                  },...response.data];
                }else{
                  return response.data;
                }
              }else{
                return [];
              }
            }),
            tap(
              () => noop,
              (err) => {
                console.log((err && err.message) || 'Something went wrong');
              }
            )
          );
        }
        return of([]);
      })
    );
  }

  upsertAddressbook(request: UpsertAddressBookRequestDto): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/address-book/upsert`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  checkPDFFilePrintandPost(request: CheckPDFFilePrintandPostRequestDto): Observable<CheckPDFFilePrintandPostResponseDto> {
    return this.http
      .post<CheckPDFFilePrintandPostResponseDto>(`${this.config.printAndPostEndpoint}/core/service.php`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  deleteAddressbook(request: DeleteAddressBookRequestDtoGet): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/address-book/delete`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }

  getDocumentManagementStorage(cpid : string): Observable<StorageUsageInfo> {
    return this.http
      .get<StorageUsageInfo>(`${this.config.documentEndpoint}/storage-usage/${cpid}`)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  } 

  downloadAddressBookCSVTemplate(): Observable<HttpResponse<Blob>>{
    return this.http.get(
      `${this.config.apiEndpoint}/address-book/download-file`,
      {
        responseType:'blob',
        observe: 'response',
      });
  }

  uploadAddressbookCSV(request: FormData): Observable<StandardResponse<String>> {
    return this.http
      .post<StandardResponse<String>>(`${this.config.apiEndpoint}/address-book/upload-file`, request)
      .pipe(retry(this.config.apiRetry), timeout(this.config.apiDigitalMailboxTimeout));
  }
 
} 
